import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SettingIcon, LogoutIcon } from "@components/icons";
import { useSelector } from "react-redux";
import { useTour } from "@reactour/tour";
import { hasPermission } from "@helper/permissions";
import {
  TreePine,
  Activity,
  ChevronDown,
  ChevronRight,
  BarChart3,
  ChevronLeft,
  LineGraph,
  CheckSquare,
  HelpCircle,
  Folder,
} from "./NavigationIcons";
import "./dual-sidebar.css";

const MainMenuSidebar = ({ isOpen, isMobile, onClose, toggleButtonRef, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = useSelector((state) => state.auth.user || {});
  const { paid } = authUser || {};
  const canViewUsers = hasPermission(authUser, "view_users");
  const canViewSettings = hasPermission(authUser, "view_settings");
  const isBasicUser = !canViewUsers;
  const canAccessSettings = canViewSettings || isBasicUser;
  const canViewCompanyInfo = hasPermission(authUser, "view_company_info");
  const [expandedMenu, setExpandedMenu] = useState(null);
  const sidebarRef = useRef(null);
  const { setIsOpen: setTourOpen, setCurrentStep } = useTour();

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleClickOutside = (event) => {
      const clickedInsideSidebar = sidebarRef.current?.contains(event.target);
      const clickedToggleButton = toggleButtonRef?.current?.contains(event.target);

      if (!clickedInsideSidebar && !clickedToggleButton) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const menuItems = [
    {
      id: "dashboard",
      title: t("dashboard.title"),
      icon: <BarChart3 size={20} />,
      path: "/dashboard",
    },
    {
      id: "trees",
      title: t("tree_cadastre"),
      icon: <TreePine size={20} />,
      path: "/trees",
      requiredPermission: "view_tree",
      submenu: [
        { label: t("tree_inventory"), path: "/trees" },
        { label: t("map_view"), path: "/map", requiredPermission: "view_tree" },
        // { label: t("areas"), path: "/areas" },
        // { label: t("inspections"), path: "/trees" },
      ],
    },
    {
      id: "sensors",
      title: t("sensors_monitoring"),
      icon: <Activity size={20} />,
      path: "/sensors",
    },
    {
      id: "projects",
      title: t("projects"),
      icon: <Folder size={20} />,
      path: "/projects",
    },
    {
      id: "tasks",
      title: t("tasks_workflows"),
      icon: <CheckSquare size={20} />,
      path: "/tasks",
    },
    // {
    //   id: "areas",
    //   title: t("areas"),
    //   icon: <MapIcon size={20} />,
    //   path: "/areas",
    // },
    {
      id: "datascience",
      title: t("data_analysis"),
      icon: <LineGraph size={20} />,
      path: "/data-science",
      requiredPermission: "view_data_analysis",
    },
    // Reporting is temporarily disabled.
    // Keep this block for quick re-enable in the future.
    // ...(showReporting
    //   ? [
    //       {
    //         id: "reporting",
    //         title: t("reporting"),
    //         icon: <FileText size={18} />,
    //         path: "/reporting",
    //       },
    //     ]
    //   : []),
  ];
  const settingsSubmenu = [
    ...(isBasicUser ? [{ label: t("profile"), path: "/settings/profile" }] : []),
    ...(paid ? [{ label: t("notifications"), path: "/settings/notifications" }] : []),
    { label: t("appearance") || "Appearance", path: "/settings/appearance" },
    ...(canViewUsers ? [{ label: t("users"), path: "/settings/users" }] : []),
    // Irrigation settings are temporarily disabled.
    // ...(account_type === 0 || account_type === 1
    //   ? [{ label: t("irrigation_settings"), path: "/settings/irrigation" }]
    //   : []),
    ...(canViewCompanyInfo ? [{ label: t("customer"), path: "/settings/customers" }] : []),
  ];

  const filteredMenuItems = menuItems
    .filter((item) => !item.requiredPermission || hasPermission(authUser, item.requiredPermission))
    .map((item) => ({
      ...item,
      submenu: (item.submenu || []).filter(
        (subitem) =>
          !subitem.requiredPermission || hasPermission(authUser, subitem.requiredPermission)
      ),
    }));

  const handleMenuClick = (item) => {
    if (typeof item.action === "function") {
      item.action();
      if (isMobile && onClose) onClose();
      return;
    }
    if (item.submenu && item.submenu.length > 0) {
      setExpandedMenu(expandedMenu === item.id ? null : item.id);
    } else {
      navigate(item.path);
      if (isMobile && onClose) onClose();
    }
  };

  const handleSubmenuClick = (path) => {
    navigate(path);
    if (isMobile && onClose) onClose();
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <>
      <aside
        ref={sidebarRef}
        className={`main-menu-sidebar ${isOpen ? "open" : ""} ${isMobile ? "mobile" : ""}`}
        data-tour="main-nav"
      >
        <div className="main-menu-content">
          {filteredMenuItems.map((item) => (
            <div key={item.id} className="menu-item-wrapper">
              <button
                className={`menu-item ${isActive(item.path) ? "active" : ""}`}
                onClick={() => handleMenuClick(item)}
              >
                <span className="menu-item-icon">{item.icon}</span>
                <span className="menu-item-title">{item.title}</span>
                {item.submenu && item.submenu.length > 0 && (
                  <span className="menu-item-chevron">
                    {expandedMenu === item.id ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </span>
                )}
              </button>

              {item.submenu && item.submenu.length > 0 && expandedMenu === item.id && (
                <div className="submenu">
                  {item.submenu.map((subitem, index) => (
                    <button
                      key={index}
                      className={`submenu-item ${isActive(subitem.path) ? "active" : ""}`}
                      onClick={() => handleSubmenuClick(subitem.path)}
                    >
                      {subitem.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="main-menu-footer">
          {canAccessSettings && (
            <div className="menu-item-wrapper">
              <button
                className={`menu-item ${isActive("/settings") ? "active" : ""}`}
                onClick={() =>
                  handleMenuClick({
                    id: "settings",
                    path: "/settings",
                    submenu: settingsSubmenu,
                  })
                }
              >
                <span className="menu-item-icon">
                  <SettingIcon size={"1.2em"} />
                </span>
                <span className="menu-item-title">{t("settings")}</span>
                <span className="menu-item-chevron">
                  {expandedMenu === "settings" ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                </span>
              </button>
              {expandedMenu === "settings" && (
                <div className="submenu">
                  {settingsSubmenu.map((subitem, index) => (
                    <button
                      key={index}
                      className={`submenu-item ${
                        location.pathname === subitem.path ? "active" : ""
                      }`}
                      onClick={() => handleSubmenuClick(subitem.path)}
                    >
                      {subitem.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="menu-item-wrapper">
            <button
              className="menu-item"
              onClick={() => setExpandedMenu(expandedMenu === "help" ? null : "help")}
            >
              <span className="menu-item-icon">
                <HelpCircle size={18} />
              </span>
              <span className="menu-item-title">{t("help")}</span>
              <span className="menu-item-chevron">
                {expandedMenu === "help" ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            </button>
            {expandedMenu === "help" && (
              <div className="submenu">
                <button
                  className={`submenu-item ${isActive("/feedback") ? "active" : ""}`}
                  onClick={() => handleSubmenuClick("/feedback")}
                >
                  {t("feedback")}
                </button>
                {paid && (
                  <button
                    className="submenu-item"
                    onClick={() => {
                      setCurrentStep(0);
                      setTourOpen(true);
                      if (onClose) onClose();
                    }}
                  >
                    {t("tour_start")}
                  </button>
                )}
                <button
                  className="submenu-item"
                  onClick={() => {
                    window.open("https://api.treesense.net/docs", "_blank", "noopener,noreferrer");
                    if (isMobile && onClose) onClose();
                  }}
                >
                  {t("api_documentation")}
                </button>
                <button
                  className="submenu-item"
                  onClick={() => {
                    window.open(
                      "https://www.treesense.net/wiki_categories/treesense-cloud/",
                      "_blank",
                      "noopener,noreferrer"
                    );
                    if (isMobile && onClose) onClose();
                  }}
                >
                  {t("wiki_help")}
                </button>
              </div>
            )}
          </div>
          <button
            className="menu-item"
            onClick={() => handleMenuClick({ id: "logout", action: onLogout })}
          >
            <span className="menu-item-icon">
              <LogoutIcon width={"1.2em"} />
            </span>
            <span className="menu-item-title">{t("logout")}</span>
          </button>
        </div>
      </aside>

      {isOpen && onClose && (
        <button className="sidebar-collapse-btn" onClick={onClose} title={t("close")}>
          <ChevronLeft size={20} />
        </button>
      )}
    </>
  );
};

export default MainMenuSidebar;
